// src/components/signage/CampaignModal.js
// Create / Edit Campaign Modal — Backend API version
// Features: Name, Description, Drag & Drop media upload, slide ordering, per-slide timers,
// orientation picker, day schedule, preview, save & apply, save, cancel
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import { RiDragMoveFill } from 'react-icons/ri';
import { MdDelete, MdCloudUpload, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import { BsPlayCircle, BsCameraVideo, BsImage } from 'react-icons/bs';
import { FiMonitor, FiSmartphone } from 'react-icons/fi';
import CampaignPreview from './CampaignPreview';
import { AuthImage } from './AuthMedia';
import {
    createCampaign,
    updateCampaign,
    uploadSlide,
    updateSlide,
    deleteSlide,
    setActiveCampaign,
} from '../../services/signageService';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
Alert.displayName = 'CampaignModalAlert';

const MAX_SLIDES = 15;

// Instagram standard dimensions reference
// Portrait: 1080 x 1350 (4:5)
// Landscape: 1080 x 566 (1.91:1)
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/webm'];

const DAY_OPTIONS = [
    { key: 'lunes', label: 'Lun' },
    { key: 'martes', label: 'Mar' },
    { key: 'miercoles', label: 'Mié' },
    { key: 'jueves', label: 'Jue' },
    { key: 'viernes', label: 'Vie' },
    { key: 'sabado', label: 'Sáb' },
    { key: 'domingo', label: 'Dom' },
];

const QUICK_SCHEDULES = [
    { label: 'Lun-Jue', days: ['lunes', 'martes', 'miercoles', 'jueves'] },
    { label: 'Viernes', days: ['viernes'] },
    { label: 'Fin de semana', days: ['sabado', 'domingo'] },
    { label: 'Todos los días', days: [] },
];

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Function} props.onSaved - Called after successful save (no args)
 * @param {Object|null} props.editCampaign - If editing, the existing campaign
 * @param {Array} props.allCampaigns - All campaigns for overlap detection
 */
function CampaignModal({ open, onClose, onSaved, editCampaign, allCampaigns = [] }) {
    const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
    // ── Form State ──
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [orientation, setOrientation] = useState('landscape');
    const [slides, setSlides] = useState([]);
    const [scheduleDays, setScheduleDays] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [isSaving, setIsSaving] = useState(false);
    const [saveProgress, setSaveProgress] = useState('');

    const fileInputRef = useRef(null);
    const dragItemRef = useRef(null);
    const dragOverItemRef = useRef(null);

    // ── Initialize from editCampaign ──
    useEffect(() => {
        if (editCampaign) {
            setName(editCampaign.name || '');
            setDescription(editCampaign.description || '');
            setOrientation(editCampaign.orientation || 'landscape');
            setSlides(editCampaign.slides || []);
            setScheduleDays(editCampaign.schedule?.days || []);
        } else {
            setName('');
            setDescription('');
            setOrientation('landscape');
            setSlides([]);
            setScheduleDays([]);
        }
        setShowPreview(false);
    }, [editCampaign, open]);

    // ── Overlap Detection ──
    const overlappingCampaigns = useMemo(() => {
        const otherCampaigns = allCampaigns.filter(c => c.id !== editCampaign?.id);
        if (otherCampaigns.length === 0) return [];

        const currentDays = scheduleDays.length === 0
            ? DAY_OPTIONS.map(d => d.key)
            : scheduleDays;

        return otherCampaigns
            .map(c => {
                const otherDays = (!c.schedule?.days || c.schedule.days.length === 0)
                    ? DAY_OPTIONS.map(d => d.key)
                    : c.schedule.days;
                const overlap = currentDays.filter(d => otherDays.includes(d));
                return overlap.length > 0 ? { campaign: c, days: overlap } : null;
            })
            .filter(Boolean);
    }, [scheduleDays, allCampaigns, editCampaign]);

    // ── Snackbar ──
    const showSnackbar = useCallback((message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    // ── File Processing ──
    const processFiles = useCallback((files) => {
        const remaining = MAX_SLIDES - slides.length;
        if (remaining <= 0) {
            showSnackbar(`Máximo ${MAX_SLIDES} slides permitidos.`, 'warning');
            return;
        }

        const filesToProcess = Array.from(files).slice(0, remaining);

        filesToProcess.forEach(file => {
            if (!ACCEPTED_TYPES.includes(file.type)) {
                showSnackbar(`Tipo no soportado: ${file.name}. Use JPG, PNG, WebP, GIF, MP4 o WebM.`, 'error');
                return;
            }

            const isVideo = file.type.startsWith('video/');
            const blobUrl = URL.createObjectURL(file);
            const newSlide = {
                id: `slide_temp_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                order: slides.length + 1,
                type: isVideo ? 'video' : 'image',
                src: blobUrl,
                filename: file.name,
                duration: isVideo ? 10 : 5,
                thumbnail: isVideo ? null : blobUrl,
                file: file,    // Raw File for upload
                isNew: true,   // Flag: not yet on server
            };

            // For video, generate a thumbnail from the first frame
            if (isVideo) {
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.onloadeddata = () => {
                    video.currentTime = 0.5;
                };
                video.onseeked = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = 120;
                    canvas.height = 80;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    const thumbDataUrl = canvas.toDataURL('image/jpeg', 0.6);
                    setSlides(prev => {
                        const updated = [...prev];
                        const idx = updated.findIndex(s => s.id === newSlide.id);
                        if (idx >= 0) updated[idx] = { ...updated[idx], thumbnail: thumbDataUrl };
                        return updated;
                    });
                    video.remove();
                };
                video.src = blobUrl;
            }

            setSlides(prev => [...prev, newSlide]);
        });

        if (Array.from(files).length > remaining) {
            showSnackbar(`Solo se agregaron ${remaining} archivos. Máximo ${MAX_SLIDES} slides.`, 'warning');
        }
    }, [slides.length, showSnackbar]);

    // ── Drag & Drop Handlers (for file upload zone) ──
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            processFiles(e.dataTransfer.files);
        }
    }, [processFiles]);

    const handleFileInputChange = useCallback((e) => {
        if (e.target.files && e.target.files.length > 0) {
            processFiles(e.target.files);
        }
        // Reset file input so same file can be re-selected
        e.target.value = '';
    }, [processFiles]);

    // ── Slide Reordering (drag in list) ──
    const handleSlideDragStart = useCallback((index) => {
        dragItemRef.current = index;
    }, []);

    const handleSlideDragEnterItem = useCallback((index) => {
        dragOverItemRef.current = index;
    }, []);

    const handleSlideDragEnd = useCallback(() => {
        if (dragItemRef.current === null || dragOverItemRef.current === null) return;
        if (dragItemRef.current === dragOverItemRef.current) return;

        setSlides(prev => {
            const updated = [...prev];
            const draggedItem = updated.splice(dragItemRef.current, 1)[0];
            updated.splice(dragOverItemRef.current, 0, draggedItem);
            // Update order values
            return updated.map((s, i) => ({ ...s, order: i + 1 }));
        });

        dragItemRef.current = null;
        dragOverItemRef.current = null;
    }, []);

    // ── Slide Actions ──
    const handleRemoveSlide = useCallback((slideId) => {
        setSlides(prev => {
            const removed = prev.find(s => s.id === slideId);
            if (removed?.isNew && removed.src?.startsWith('blob:')) {
                URL.revokeObjectURL(removed.src);
            }
            return prev.filter(s => s.id !== slideId).map((s, i) => ({ ...s, order: i + 1 }));
        });
    }, []);

    const handleMoveSlide = useCallback((index, direction) => {
        setSlides(prev => {
            const updated = [...prev];
            const newIndex = index + direction;
            if (newIndex < 0 || newIndex >= updated.length) return prev;
            [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
            return updated.map((s, i) => ({ ...s, order: i + 1 }));
        });
    }, []);

    const handleDurationChange = useCallback((slideId, value) => {
        const num = parseInt(value, 10);
        if (isNaN(num) || num < 1) return;
        if (num > 120) return;
        setSlides(prev => prev.map(s => s.id === slideId ? { ...s, duration: num } : s));
    }, []);

    // ── Schedule ──
    const toggleDay = useCallback((dayKey) => {
        setScheduleDays(prev =>
            prev.includes(dayKey) ? prev.filter(d => d !== dayKey) : [...prev, dayKey]
        );
    }, []);

    const applyQuickSchedule = useCallback((days) => {
        setScheduleDays(days);
    }, []);

    // ── Validation ──
    const isValid = name.trim().length > 0 && slides.length > 0;

    // ── Save logic (calls backend APIs directly) ──
    const doSave = useCallback(async (shouldApply) => {
        if (!isValid || isSaving) return;
        setIsSaving(true);
        setSaveProgress('');

        try {
            const metadata = {
                name: name.trim(),
                description: description.trim(),
                orientation,
                schedule: { days: scheduleDays },
            };

            if (editCampaign) {
                // ── EDIT MODE ──
                const originalIds = (editCampaign.slides || []).map(s => s.id);
                const keptIds = slides.filter(s => !s.isNew).map(s => s.id);
                const removedIds = originalIds.filter(id => !keptIds.includes(id));
                const newSlides = slides.filter(s => s.isNew);

                // 1. Delete removed slides
                for (const id of removedIds) {
                    setSaveProgress('Eliminando slides...');
                    await deleteSlide(id);
                }

                // 2. Upload new slides
                const idMapping = {};
                for (let i = 0; i < newSlides.length; i++) {
                    const slide = newSlides[i];
                    setSaveProgress(`Subiendo ${i + 1}/${newSlides.length}...`);
                    const result = await uploadSlide(editCampaign.id, slide.file, slide.duration);
                    idMapping[slide.id] = result.id;
                }

                // 3. Update durations for existing slides that changed
                for (const slide of slides.filter(s => !s.isNew)) {
                    const original = (editCampaign.slides || []).find(os => os.id === slide.id);
                    if (original && original.duration !== slide.duration) {
                        await updateSlide(slide.id, { duration: slide.duration });
                    }
                }

                // 4. Build final slide order and update metadata
                const slideOrder = slides.map(s => s.isNew ? idMapping[s.id] : s.id);
                setSaveProgress('Guardando campaña...');
                await updateCampaign(editCampaign.id, { ...metadata, slideOrder });

                // 5. Activate if requested
                if (shouldApply) {
                    await setActiveCampaign(editCampaign.id);
                }
            } else {
                // ── CREATE MODE ──
                setSaveProgress('Creando campaña...');
                const campaign = await createCampaign(metadata);

                // Upload each slide
                const newSlides = slides.filter(s => s.file);
                for (let i = 0; i < newSlides.length; i++) {
                    const slide = newSlides[i];
                    setSaveProgress(`Subiendo ${i + 1}/${newSlides.length}...`);
                    await uploadSlide(campaign.id, slide.file, slide.duration);
                }

                // Activate if requested
                if (shouldApply) {
                    await setActiveCampaign(campaign.id);
                }
            }

            // Cleanup blob URLs
            slides.forEach(s => {
                if (s.isNew && s.src?.startsWith('blob:')) URL.revokeObjectURL(s.src);
            });

            if (onSaved) onSaved();
        } catch (err) {
            const msg = err.response?.data?.error || err.message;
            showSnackbar('Error: ' + msg, 'error');
        } finally {
            setIsSaving(false);
            setSaveProgress('');
        }
    }, [isValid, isSaving, name, description, orientation, scheduleDays, slides, editCampaign, onSaved, showSnackbar]);

    // ── Save handlers ──
    const handleSave = useCallback(() => doSave(false), [doSave]);
    const handleSaveAndApply = useCallback(() => doSave(true), [doSave]);

    // ── Build preview campaign object (for in-modal preview) ──
    const previewCampaign = showPreview ? {
        name: name.trim(),
        description: description.trim(),
        orientation,
        slides: slides.map((s, i) => ({ ...s, order: i + 1 })),
        schedule: { days: scheduleDays },
    } : null;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px', maxHeight: '90vh' } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', pb: 1 }}>
                    {editCampaign ? 'Editar Campaña' : 'Nueva Campaña'}
                </DialogTitle>

                <DialogContent dividers sx={{ p: 3 }}>
                    <div className="campaign-modal-content">
                        {/* ── Name ── */}
                        <div className="campaign-modal-field">
                            <label>Nombre de la Campaña *</label>
                            <TextField
                                fullWidth
                                size="small"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="Ej: Promo Verano 2026"
                                inputProps={{ maxLength: 80 }}
                                disabled={isDemoMode}
                            />
                        </div>

                        {/* ── Description ── */}
                        <div className="campaign-modal-field">
                            <label>Descripción</label>
                            <TextField
                                fullWidth
                                size="small"
                                multiline
                                rows={2}
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Breve descripción de la campaña"
                                inputProps={{ maxLength: 200 }}
                                disabled={isDemoMode}
                            />
                        </div>

                        {/* ── Orientation ── */}
                        <div className="campaign-modal-field">
                            <label>Orientación</label>
                            <div className="signage-orientation-picker">
                                <div
                                    className={`signage-orientation-option ${orientation === 'landscape' ? 'selected' : ''}`}
                                    onClick={() => !isDemoMode && setOrientation('landscape')}
                                    style={isDemoMode ? { pointerEvents: 'none', opacity: 0.5 } : {}}
                                >
                                    <FiMonitor className="orient-icon" />
                                    <span>Horizontal</span>
                                </div>
                                <div
                                    className={`signage-orientation-option ${orientation === 'portrait' ? 'selected' : ''}`}
                                    onClick={() => !isDemoMode && setOrientation('portrait')}
                                    style={isDemoMode ? { pointerEvents: 'none', opacity: 0.5 } : {}}
                                >
                                    <FiSmartphone className="orient-icon" />
                                    <span>Vertical</span>
                                </div>
                            </div>
                        </div>

                        {/* ── Media Upload (Drag & Drop) ── */}
                        <div className="campaign-modal-field">
                            <label>Imágenes / Videos ({slides.length}/{MAX_SLIDES})</label>

                            {slides.length < MAX_SLIDES && !isDemoMode && (
                                <div
                                    className={`signage-dropzone ${isDragOver ? 'drag-over' : ''}`}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <MdCloudUpload className="drop-icon" />
                                    <p><strong>Arrastrá archivos aquí</strong> o hacé click para seleccionar</p>
                                    <p style={{ fontSize: '0.78rem' }}>
                                        JPG, PNG, WebP, GIF, MP4, WebM — {orientation === 'portrait' ? '1080 × 1350 px (4:5)' : '1080 × 566 px (1.91:1)'}
                                    </p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept={ACCEPTED_TYPES.join(',')}
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={handleFileInputChange}
                                    />
                                </div>
                            )}

                            {/* ── Slides List ── */}
                            {slides.length > 0 && (
                                <div className="signage-slides-list">
                                    {slides.map((slide, index) => (
                                        <div
                                            key={slide.id}
                                            className="signage-slide-item"
                                            draggable
                                            onDragStart={() => handleSlideDragStart(index)}
                                            onDragEnter={() => handleSlideDragEnterItem(index)}
                                            onDragEnd={handleSlideDragEnd}
                                            onDragOver={e => e.preventDefault()}
                                        >
                                            <RiDragMoveFill style={{ color: '#bbb', fontSize: '18px', flexShrink: 0 }} />

                                            {slide.thumbnail ? (
                                                <AuthImage
                                                    src={slide.thumbnail}
                                                    alt={slide.filename}
                                                    className="slide-thumb"
                                                />
                                            ) : (
                                                <div className="slide-thumb" style={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                }}>
                                                    <BsCameraVideo style={{ color: '#fff' }} />
                                                </div>
                                            )}

                                            <div className="slide-info">
                                                <div className="slide-name">{slide.filename}</div>
                                                <div className="slide-type">
                                                    {slide.type === 'video' ? (
                                                        <><BsCameraVideo size={10} /> Video</>
                                                    ) : (
                                                        <><BsImage size={10} /> Imagen</>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="slide-timer">
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={120}
                                                    value={slide.duration}
                                                    onChange={e => handleDurationChange(slide.id, e.target.value)}
                                                    title="Duración en segundos"
                                                    disabled={isDemoMode}
                                                />
                                                <span style={{ fontSize: '0.75rem', color: '#999' }}>seg</span>
                                            </div>

                                            <div className="slide-actions">
                                                <Tooltip title="Subir">
                                                    <IconButton size="small" onClick={() => handleMoveSlide(index, -1)} disabled={index === 0 || isDemoMode}>
                                                        <MdArrowUpward />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Bajar">
                                                    <IconButton size="small" onClick={() => handleMoveSlide(index, 1)} disabled={index === slides.length - 1 || isDemoMode}>
                                                        <MdArrowDownward />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Eliminar">
                                                    <IconButton size="small" onClick={() => handleRemoveSlide(slide.id)} color="error" disabled={isDemoMode}>
                                                        <MdDelete />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* ── Schedule ── */}
                        <div className="campaign-modal-field">
                            <label>Programación (días)</label>
                            <div style={{ marginBottom: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {QUICK_SCHEDULES.map(qs => (
                                    <Button
                                        key={qs.label}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => applyQuickSchedule(qs.days)}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                        disabled={isDemoMode}
                                    >
                                        {qs.label}
                                    </Button>
                                ))}
                            </div>
                            <div className="signage-schedule-picker">
                                {DAY_OPTIONS.map(day => (
                                    <div
                                        key={day.key}
                                        className={`signage-schedule-day ${scheduleDays.includes(day.key) ? 'selected' : ''}`}
                                        onClick={() => !isDemoMode && toggleDay(day.key)}
                                        style={isDemoMode ? { pointerEvents: 'none', opacity: 0.5 } : {}}
                                    >
                                        {day.label}
                                    </div>
                                ))}
                            </div>
                            <p style={{ fontSize: '0.75rem', color: '#999', marginTop: 4 }}>
                                {scheduleDays.length === 0
                                    ? 'Sin selección = todos los días'
                                    : `Activa: ${scheduleDays.map(d => DAY_OPTIONS.find(o => o.key === d)?.label).join(', ')}`
                                }
                            </p>
                            {overlappingCampaigns.length > 0 && (
                                <div style={{
                                    marginTop: 8,
                                    padding: '8px 12px',
                                    background: '#fff3e0',
                                    border: '1px solid #ffb74d',
                                    borderRadius: 6,
                                    fontSize: '0.8rem',
                                    color: '#e65100',
                                }}>
                                    <strong>⚠ Superposición de horarios:</strong>
                                    <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                                        {overlappingCampaigns.map(({ campaign: c, days }) => (
                                            <li key={c.id}>
                                                <strong>{c.name}</strong>: {days.map(d => DAY_OPTIONS.find(o => o.key === d)?.label).join(', ')}
                                            </li>
                                        ))}
                                    </ul>
                                    <p style={{ margin: '4px 0 0', fontSize: '0.75rem' }}>
                                        Solo una campaña puede estar activa. Si ambas cubren el mismo día, el Totem no mostrará cartelería.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* ── Preview Toggle ── */}
                        <div className="campaign-modal-field">
                            <Button
                                variant="outlined"
                                startIcon={<BsPlayCircle />}
                                onClick={() => setShowPreview(!showPreview)}
                                disabled={slides.length === 0}
                                sx={{ textTransform: 'none' }}
                            >
                                {showPreview ? 'Ocultar Vista Previa' : 'Vista Previa'}
                            </Button>

                            {showPreview && previewCampaign && (
                                <div style={{ marginTop: 12 }}>
                                    <CampaignPreview campaign={previewCampaign} />
                                </div>
                            )}
                        </div>
                    </div>
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
                    {isSaving && (
                        <div style={{ width: '100%', textAlign: 'center' }}>
                            <LinearProgress sx={{ mb: 1 }} />
                            <span style={{ fontSize: '0.8rem', color: '#666' }}>{saveProgress}</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button onClick={onClose} disabled={isSaving} sx={{ textTransform: 'none' }}>
                            Cancelar
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleSave}
                            disabled={!isValid || isSaving || isDemoMode}
                            sx={{ textTransform: 'none' }}
                        >
                            Guardar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveAndApply}
                            disabled={!isValid || isSaving || isDemoMode}
                            sx={{ textTransform: 'none' }}
                        >
                            Guardar y Aplicar
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default CampaignModal;
